import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils'
import { Phone, Mail, Clock, DollarSign, GripVertical } from 'lucide-react'
import { LeadScoreBadge, LeadSourceBadge } from '@/components/leads'
import type { LeadWithRelations } from '@limo/shared'

interface LeadCardProps {
  lead: LeadWithRelations
  onClick?: () => void
  isDragging?: boolean
}

// Format helpers
const formatCurrency = (amount: number | null | undefined) =>
  amount != null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : null

const formatRelativeDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return null
  const now = new Date()
  const date = new Date(dateStr)
  const diffDays = Math.ceil((date.getTime() - now.getTime()) / 86400000)

  if (diffDays < 0) return 'Overdue'
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays < 7) return `In ${diffDays}d`
  return null
}

export function LeadCard({ lead, onClick, isDragging }: LeadCardProps) {
  const followUpText = formatRelativeDate(lead.nextFollowUpAt)
  const isOverdue = lead.nextFollowUpAt && new Date(lead.nextFollowUpAt) < new Date()
  const value = formatCurrency(lead.estimatedValue)

  return (
    <div
      className={cn(
        'bg-card rounded-lg border p-3 cursor-pointer transition-all',
        'hover:shadow-md hover:border-primary/30',
        isDragging && 'shadow-lg ring-2 ring-primary/50 rotate-2'
      )}
      onClick={onClick}
    >
      {/* Header: Name & Score */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-sm truncate flex-1">{lead.customerName}</h4>
        <LeadScoreBadge score={lead.score} showLabel={false} size="sm" />
      </div>

      {/* Contact Info */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
        {lead.customerPhone && (
          <span className="flex items-center gap-1">
            <Phone className="h-3 w-3" />
          </span>
        )}
        {lead.customerEmail && (
          <span className="flex items-center gap-1">
            <Mail className="h-3 w-3" />
          </span>
        )}
        <LeadSourceBadge source={lead.source} showIcon={true} className="text-xs py-0 px-1.5" />
      </div>

      {/* Value & Follow-up */}
      <div className="flex items-center justify-between text-xs">
        {value && (
          <span className="flex items-center gap-1 font-medium">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            {value}
          </span>
        )}
        {followUpText && (
          <span className={cn(
            'flex items-center gap-1',
            isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
          )}>
            <Clock className="h-3 w-3" />
            {followUpText}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Sortable version of LeadCard for drag-and-drop
 */
export function SortableLeadCard({ lead, onClick }: LeadCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div className="relative group">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="pl-4">
          <LeadCard lead={lead} onClick={onClick} isDragging={isDragging} />
        </div>
      </div>
    </div>
  )
}
